import React, {
    Children,
    useEffect,
    useRef,
    useState,
    useContext,
    useCallback,
    useMemo,
} from "react";
import "./NFLookupCatalog.css";
import {TextBox, Button as TextBoxButton} from "devextreme-react/text-box";
import ScrollView from "devextreme-react/scroll-view";
import {Popover} from "devextreme-react";
import {
    Controller,
    useFormContext,
    useFormState,
    useWatch,
} from "react-hook-form";
import {ValidationStatus} from "devextreme/common";
import {NewCatalogModalView} from "../NewCatalogModalView";
import {CompanyDetail} from "../../Pages/Catalogs/Company/CompanyDetail";
import DropDownArrowIcon from "../../../assets/img/svg/DropDownArrowIcon";
import Search from "../../../assets/img/svg/Search";
import Loader from "../../../assets/img/svg/Loader";
import {useAppDispatch, useAppSelector} from "../../../redux/hooks/hooks";
import {setModalData} from "../../../redux/slices/modalSlice";
import {setIsTabModified} from "../../../redux/slices/newTabSlice";

interface IProps {
    title?: string;
    fieldName: string;
    entity: any;
    children: React.ReactNode;
    serverData?: (searchText: string | undefined) => Promise<any[]>;
    required?: boolean;
    showBorder?: boolean;
    width?: number;
    index?: number;
    tableName?: string;
    tableFieldName?: string;
    onValueChanged?: (e: any, index: number) => void;
    value?: any;
    displayFieldName?: string;
    selectedCatalog?: boolean;
    setSelectedCatalog?: (value: boolean) => void;
    onModalSelect?: (data: any) => void;
    modelField?: string;
}

export const NFLookupCatalog = ({
                                    fieldName,
                                    entity,
                                    title,
                                    children,
                                    serverData,
                                    required,
                                    showBorder = true,
                                    width,
                                    index = 0,
                                    tableFieldName,
                                    tableName,
                                    onValueChanged,
                                    value,
                                    displayFieldName = "name",
                                    selectedCatalog,
                                    setSelectedCatalog,
                                    onModalSelect,
                                    modelField,
                                }: IProps) => {
    const {register, control, watch, formState, trigger, setValue, getValues} =
        useFormContext();
    const {errors, dirtyFields, isDirty} = useFormState();

    const [currentId, setCurrentId] = useState<string | undefined>(undefined);
    const [loadModel, setLoadModel] = useState<any[]>([]);
    // const activeTabId = useAppSelector((state: any) => state.tabs.activeTabId);
    const activeTabId = useAppSelector((state: any) => state.newTab.activeTab);
    const dispatch = useAppDispatch();

    const [validationStatus, setValidationStatus] =
        useState<ValidationStatus>("valid");

    const [selectedItem, setSelectedItem] = useState<any>(undefined);
    const [isPopoverVisible, setIsPopoverVisible] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>("");
    const [selectedItemId, setSelectedItemId] = useState<string>("");
    const [catalogModalViewOpen, setCatalogModalViewOpen] =
        useState<boolean>(false);
    const [isDeleteButtonClicked, setIsDeleteButtonClicked] = useState(false);

    const [detailModalViewOpen, setDetailModalViewOpen] =
        useState<boolean>(false);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const [items, setItems] = useState<any[]>([]);
    const popoverRef = useRef<Popover>(null);
    const componentId = useMemo(
        () =>
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15),
        []
    );

    const arrayChildren = useMemo(() => Children.toArray(children), [children]);

    const loadItems = useCallback(
        async (searchText: string | undefined) => {
            // In addition to name, I added fieldName and userName to make our NFlookups work properly.
            try {
                setIsLoading(true);
                setIsError(false);
                setErrorMessage("");

                let filteredItems: any[] = [];
                if (serverData) {
                    const loadedItems = await serverData(searchText);
                    setLoadModel(loadedItems);

                    filteredItems = loadedItems.filter(
                        (item) =>
                            item.name
                                ?.toLowerCase()
                                .includes(searchText?.toLowerCase() ?? "") ||
                            item.userName
                                ?.toLowerCase()
                                .includes(searchText?.toLowerCase() ?? "") ||
                            item.firstName
                                ?.toLowerCase()
                                .includes(searchText?.toLowerCase() ?? "") ||
                            item.accountNo
                                ?.toLowerCase()
                                .includes(searchText?.toLowerCase() ?? "")
                    );
                } else if (entity) {
                    const dataService = entity.getDataService();
                    const loadedItems = await dataService.GetAll();

                    filteredItems = loadedItems.filter(
                        (item: any) =>
                            item.name
                                ?.toLowerCase()
                                .includes(searchText?.toLowerCase() ?? "") ||
                            item.userName
                                ?.toLowerCase()
                                .includes(searchText?.toLowerCase() ?? "") ||
                            item.firstName
                                ?.toLowerCase()
                                .includes(searchText?.toLowerCase() ?? "")
                    );
                }

                setItems(filteredItems);
            } catch (e: any) {
                setIsError(true);
                setErrorMessage(e.message);
                setItems([]);
            } finally {
                setIsLoading(false);
            }
        },
        [entity, serverData]
    );

    const loadDefaultItem = useCallback(async () => {
        const item = await entity.getDataService().Get(currentId);

        setSelectedItemId(item.id);
        setSelectedItem(item);
        setSearchText(item ? item[displayFieldName] : "");
    }, [currentId, displayFieldName, entity]);

    const handlePopoverHidden = useCallback(() => {
        if (!isDeleteButtonClicked && selectedItem) {
            setSearchText(selectedItem ? selectedItem[displayFieldName] : "");
        }
        setIsPopoverVisible(false);
        setIsDeleteButtonClicked(false);
    }, [selectedItem, searchText, displayFieldName, isDeleteButtonClicked]);

    const handleSelectItem = useCallback(
        (item: any) => {
            setSelectedItem(item);
            setSearchText(item ? item[displayFieldName] : "");

            setIsPopoverVisible(false);
        },
        [displayFieldName, setSelectedItem, setSearchText]
    );

    const handleSearchTextChange = async (inputEvent: any) => {
        const newSearchTextValue = inputEvent.target.value;

        if (newSearchTextValue !== searchText) {
            setSearchText(newSearchTextValue);
            if (!isPopoverVisible) setIsPopoverVisible(true);
            await loadItems(newSearchTextValue);
        }
    };

    const handleCatalogModalViewClosed = useCallback(() => {
        setCatalogModalViewOpen(false);
    }, [loadItems, searchText]);

    const handleDetailModalViewClose = useCallback(() => {
        setDetailModalViewOpen(false);
    }, []);

    const onChangeFunc = () => {
        dispatch(setIsTabModified({tabId: activeTabId, isChanged: true}));
    };
    const handleDeleteButtonClick = async () => {
        setSearchText("");
        await loadItems("");

        if (setSelectedCatalog) {
            setSelectedCatalog(false);
        }
        activeTabId && trigger(fieldName);

        setIsDeleteButtonClicked(true);
    };

    useEffect(() => {
        setCurrentId(watch(fieldName));
    }, [watch(fieldName)]);

    useEffect(() => {
        if (currentId) {
            loadDefaultItem();
        }
    }, [currentId]);

    useEffect(() => {
        if (loadModel?.[0]) {
            dispatch(setModalData(loadModel?.[0]));
        }
    }, [loadModel, dispatch]);

    useEffect(() => {
        if (tableName && tableFieldName) {
            //@ts-ignore
            const errorTable = formState.errors[tableName];
            if (errorTable) {
                //@ts-ignore
                const errorLine = errorTable[index];
                if (errorLine) {
                    const errorField = errorLine[tableFieldName];
                    if (errorField) {
                        setValidationStatus("invalid");
                        return;
                    }
                }
            }
            setValidationStatus("valid");
            // return "valid";
        } else {
            const status = formState.errors[fieldName]
                ? "invalid"
                : dirtyFields[fieldName]
                    ? "valid"
                    : "valid";
            setValidationStatus(status);
        }
    }, [formState]);

    return (
        <>
            <Controller
                control={control}
                name={fieldName}
                rules={{
                    required: required === true,
                    onChange: (newValue: any) => {
                        const newId = newValue?.target.value;
                        if (newId) {
                            const newItem = items.find((x) => x.id === newId);
                            if (modelField) {
                                setValue(`${tableFieldName}[${index}].${modelField}`, newItem);
                            }

                            handleSelectItem(newItem);
                            setSearchText(newItem[displayFieldName]);
                        } else {
                            handleSelectItem(undefined);
                            setSearchText("");
                        }
                        if (onValueChanged) {
                            onValueChanged(newValue.target.value, index);
                        }
                        onChangeFunc();
                    },
                }}
                render={({field: {onChange, onBlur, value, ref}}) => {
                    return (
                        <>
                            <div className={title ? "form-group" : ""}>
                                {title && <label className={"form-label-sm"}>{title}</label>}
                                <TextBox
                                    value={searchText}
                                    name={`searchTextBox${componentId}`}
                                    width={width || undefined}
                                    style={{border: showBorder === true ? "block" : "none"}}
                                    id={`searchTextBox${componentId}`}
                                    validationStatus={validationStatus}
                                    onInput={async (e) => {
                                        await handleSearchTextChange(e);
                                    }}
                                    className="bg-light d-flex flex-row-reverse"
                                >
                                    <div className="d-flex justify-content-end align-items-center">
                                        <div
                                            onClick={async () => {
                                                setIsPopoverVisible(!isPopoverVisible);
                                                await loadItems(searchText);
                                            }}
                                        >
                                            <DropDownArrowIcon className="nf-lookup-icon"/>
                                        </div>

                                        <div className="vr nf-lookupVr"></div>
                                        <div
                                            onClick={() => {
                                                if (selectedItem) {
                                                    setCatalogModalViewOpen(true);
                                                    setSelectedItem(selectedItem);
                                                }
                                            }}
                                        >
                                            <Search className="nf-lookup-icon"/>
                                        </div>
                                        <div className="vr nf-lookupVr"></div>
                                        <div
                                            onClick={async () => {
                                                onChange(undefined);
                                                if (isPopoverVisible) {
                                                    await loadItems("");
                                                }
                                                if (setSelectedCatalog) {
                                                    setSelectedCatalog(false);
                                                }
                                                activeTabId && trigger(fieldName);
                                            }}
                                        >
                                            <svg
                                                viewBox="0 0 48 48"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="nf-lookup-icon"
                                            >
                                                <path
                                                    d="m12.45 37.65-2.1-2.1L21.9 24 10.35 12.45l2.1-2.1L24 21.9l11.55-11.55 2.1 2.1L26.1 24l11.55 11.55-2.1 2.1L24 26.1Z"></path>
                                            </svg>
                                        </div>
                                    </div>
                                </TextBox>
                            </div>
                            <Popover
                                target={`#searchTextBox${componentId}`}
                                position={"bottom"}
                                onHidden={handlePopoverHidden}
                                ref={popoverRef}
                                visible={isPopoverVisible}
                            >
                                <div className={"lookup-catalog-popover-table popover-content"}>
                                    <div className="input-container">
                                        <input
                                            type="text"
                                            value={searchText}
                                            onChange={(e) => handleSearchTextChange(e)}
                                            className="custom-input-element"
                                        />
                                        <svg
                                            onClick={handleDeleteButtonClick}
                                            viewBox="0 0 48 48"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="nf-lookup-icon-delete"
                                        >
                                            <path
                                                d="m12.45 37.65-2.1-2.1L21.9 24 10.35 12.45l2.1-2.1L24 21.9l11.55-11.55 2.1 2.1L26.1 24l11.55 11.55-2.1 2.1L24 26.1Z"></path>
                                        </svg>
                                    </div>

                                    <ScrollView height={300}>
                                        <div className="table-wrapper-scroll-y">
                                            {isLoading && (
                                                <>
                                                    <div className="d-flex justify-content-center mt-2">
                                                        <Loader/>
                                                    </div>
                                                </>
                                            )}
                                            {!isLoading && (
                                                <table className="table bg-light">
                                                    <thead>
                                                    <tr>
                                                        {arrayChildren.map((field: any, index) => {
                                                            return (
                                                                <th key={index}>{field.props.caption}</th>
                                                            );
                                                        })}
                                                    </tr>
                                                    </thead>

                                                    <tbody>
                                                    {items?.map((item: any, index) => (
                                                        <tr
                                                            key={index}
                                                            style={{cursor: "pointer"}}
                                                            onClick={() => {
                                                                onChange(item?.id);
                                                            }}
                                                        >
                                                            {arrayChildren.map((field: any, index) => {
                                                                return (
                                                                    <td key={index}>
                                                                        {item[field.props.fieldName]}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </ScrollView>

                                    {items?.length === 0 && (
                                        <div className={"popover-text mb-3"}>No items</div>
                                    )}
                                    <div className="d-flex justify-content-center mt-2">
                                        <div
                                            className="link-dark add-new-btn"
                                            onClick={() => {
                                                setDetailModalViewOpen(true);
                                                setIsPopoverVisible(false);
                                            }}
                                        >
                                            Create new...
                                        </div>
                                    </div>
                                </div>
                            </Popover>
                            {selectedItem && catalogModalViewOpen && (
                                <NewCatalogModalView
                                    id={selectedItem.id}
                                    entity={entity}
                                    open={catalogModalViewOpen}
                                    onClose={handleCatalogModalViewClosed}
                                    setSearchText={setSearchText}
                                    setSelectedItem={setSelectedItem}
                                />
                            )}

                            {detailModalViewOpen && (
                                <NewCatalogModalView
                                    id={selectedItem?.id}
                                    entity={entity}
                                    open={detailModalViewOpen}
                                    onClose={handleDetailModalViewClose}
                                    setSearchText={setSearchText}
                                    setSelectedItem={setSelectedItem}
                                />
                            )}
                        </>
                    );
                }}
            />
        </>
    );
};
